import { cn } from '@/shared/lib/cn';
import { Card } from '@/shared/ui/core/Card';
import type { SemanticTone } from '@/shared/ui/core/types';

export type DistributionSegment = {
  id: string;
  label: string;
  count: number;
  tone?: SemanticTone;
  selected?: boolean;
  onClick?: () => void;
};

const barTone: Record<SemanticTone, string> = {
  neutral: 'bg-slate-700',
  primary: 'bg-primary',
  accent: 'bg-accent',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
  hold: 'bg-hold',
  draft: 'bg-slate-300',
};

const legendDot: Record<SemanticTone, string> = {
  neutral: 'bg-slate-700',
  primary: 'bg-primary',
  accent: 'bg-accent',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
  hold: 'bg-hold',
  draft: 'bg-slate-300',
};

export type StatusDistributionChartProps = {
  title: string;
  segments: readonly DistributionSegment[];
  className?: string;
};

/** CSS stacked bar — no chart library. Counts must come from DemoStore. */
export function StatusDistributionChart({
  title,
  segments,
  className,
}: StatusDistributionChartProps) {
  const total = segments.reduce((sum, s) => sum + s.count, 0);
  const visible = segments.filter((s) => s.count > 0);

  return (
    <Card dense className={className}>
      <p className="m-0 text-caption font-semibold text-text-secondary">{title}</p>
      {total === 0 ? (
        <p className="m-0 mt-2 text-caption text-text-muted">Không có dữ liệu để vẽ.</p>
      ) : (
        <>
          <div
            className="mt-2 flex h-3 overflow-hidden rounded-pill bg-bg"
            role="img"
            aria-label={`${title}: ${visible.map((s) => `${s.label} ${s.count}`).join(', ')}`}
          >
            {visible.map((s) => (
              <div
                key={s.id}
                className={cn('h-full min-w-0', barTone[s.tone ?? 'neutral'])}
                style={{ width: `${(s.count / total) * 100}%` }}
                title={`${s.label}: ${s.count}`}
              />
            ))}
          </div>
          <ul className="m-0 mt-2 flex list-none flex-wrap gap-x-3 gap-y-1 p-0">
            {segments.map((s) => {
              const content = (
                <>
                  <span
                    className={cn('size-2 shrink-0 rounded-full', legendDot[s.tone ?? 'neutral'])}
                    aria-hidden
                  />
                  <span>
                    {s.label}{' '}
                    <span className="font-mono tabular-nums text-text">{s.count}</span>
                  </span>
                </>
              );
              return (
                <li key={s.id}>
                  {s.onClick ? (
                    <button
                      type="button"
                      aria-pressed={s.selected || undefined}
                      onClick={s.onClick}
                      className={cn(
                        'inline-flex cursor-pointer items-center gap-1.5 rounded-control px-1.5 py-0.5 text-caption',
                        s.selected
                          ? 'font-semibold text-text ring-1 ring-(--color-focus-ring)'
                          : 'text-text-secondary hover:bg-bg',
                      )}
                    >
                      {content}
                    </button>
                  ) : (
                    <span className="flex items-center gap-1.5 text-caption text-text-secondary">
                      {content}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Card>
  );
}
